<template>
    <div>
        <div class="preloader" v-if="loading">
            <CSpinner color="primary"/>
        </div>
        <div v-show="!loading">
            <CRow v-if="adminOnly">
                <CCol xl="6">
                    <OrderDetails
                        :order="order"
                        :lastStatusUpdate="lastStatusUpdate"
                        @update="orderUpdate"
                        @paymentConfirm="paymentConfirm"
                        @copy="orderCopy"
                        @info="updateOrderInfo" />

                    <OrderPrice
                        :id="+$route.params.id"
                        :order="order"
                        :items="orderItems"
                        :payments="payments"
                        @refund="orderRefund"
                        @paymentConfirm="paymentConfirm"
                    />

                    <OrderActions
                        :id="+$route.params.id"
                        :order="order"
                    />

                    <OrderPersons
                        :id="+$route.params.id"
                        :order="order"
                    />
                </CCol>

                <CCol xl="6">
                    <OrderPayments :items="payments" />

                    <OrderReceipts 
                        :order_id="+$route.params.id"
                        :items="order.receipts" />

                    <OrderComments
                        v-if="adminOnly"
                        :id="+$route.params.id"
                        :order="order"
                    />

                    <OrderLogs 
                        :order="order"
                        :logs="logs" />

                    <OrderAnalyticsData
                        :order="order"
                        @update="orderUpdate" />
                    
                    <OrderPartnerError
                        :order="order"
                        @update="orderUpdate"/>
                    
                    <OrderDelaysAccounting
                        :order="order"
                        @update="orderUpdate"/>
                </CCol>
            </CRow>
            <CRow v-else>
                <CCol xl="6">
                    <OrderDetails
                        :order="order"
                        :lastStatusUpdate="lastStatusUpdate"
                        @update="orderUpdate"
                        @paymentConfirm="paymentConfirm"
                        @copy="orderCopy"
                        @info="updateOrderInfo" />

                </CCol>
                <CCol xl="6">
                    <OrderActions
                        :id="+$route.params.id"
                        :order="order"
                    />
                </CCol>
            </CRow>

            <CRow v-if="adminOnly">
                <CCol>
                    <OrderChat :order="order" />
                </CCol>
            </CRow>

            <CRow>
                <CCol>
                    <OrderRD
                        v-if="orderTypeId === 'rd'"
                        :order="order.rd"
                        :order-unmodified="orderUnmodified.rd"
                        :is-shop-order="order.code === 'shop'"
                        @update="orderUpdate"
                        @cancel="orderCancel"
                    />

                    <OrderLD
                        v-if="orderTypeId === 'ld'"
                        :order="order.ld"
                        :order-unmodified="orderUnmodified.ld"
                        @update="orderUpdate"
                        @cancel="orderCancel"
                    />

                    <OrderFD
                        v-if="orderTypeId === 'fd'"
                        :order="order.fd"
                        :order-unmodified="orderUnmodified.fd"
                        @update="orderUpdate"
                        @cancel="orderCancel" 
                    />

                    <OrderGD
                        v-if="orderTypeId === 'gd'"
                        :order="order.gd"
                        :order-unmodified="orderUnmodified.gd"
                        @update="orderUpdate"
                        @cancel="orderCancel"
                    />

                    <OrderPD 
                        v-if="orderTypeId === 'pd'"
                        :order="order.pd"
                        :order-unmodified="orderUnmodified.pd"
                        @update="orderUpdate"
                        @cancel="orderCancel" 
                    />

                    <OrderAH
                        v-if="orderTypeId === 'ah'"
                        :order="order.ah"
                        :order-unmodified="orderUnmodified.ah"
                        @update="orderUpdate"
                        @cancel="orderCancel" 
                    />

                    <OrderED
                        v-if="orderTypeId === 'ed'"
                        :order="order.ed"
                        :order-unmodified="orderUnmodified.ed"
                        @update="orderUpdate"
                        @cancel="orderCancel" 
                    />
                </CCol>
            </CRow>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import Vue from 'vue';
    import OrderDetails from '@/components/orders/OrderDetails';
    import OrderRD from '@/components/orders/OrderRD';
    import OrderED from '@/components/orders/OrderED';
    import OrderFD from '@/components/orders/OrderFD';
    import OrderLD from '@/components/orders/OrderLD';
    import OrderGD from '@/components/orders/OrderGD';
    import OrderPD from '@/components/orders/OrderPD';
    import OrderAH from '@/components/orders/OrderAH';
    import OrderPayments from '@/components/orders/OrderPayments';
    import OrderReceipts from '@/components/orders/OrderReceipts';
    import OrderActions from '@/components/orders/OrderActions';
    import OrderLogs from '@/components/orders/OrderLogs';
    import OrderPersons from '@/components/orders/OrderPersons';
    import OrderComments from '@/components/orders/OrderComments';
    import OrderChat from '@/components/orders/OrderChat';
    import OrderPrice from '@/components/orders/OrderPrice';
    import OrderAnalyticsData from '@/components/orders/OrderAnalyticsData';
    import OrderPartnerError from '@/components/orders/OrderPartnerError';
    import OrderDelaysAccounting from '@/components/orders/OrderDelaysAccounting';
    const { ORDER_TYPE, ORDER_PAYMENT_STATUS } = require('../../utils/order');

    export default {
        name: 'Order',

        components: {
            OrderRD,
            OrderED,
            OrderFD,
            OrderLD,
            OrderGD,
            OrderPD,
            OrderAH,
            OrderPayments,
            OrderReceipts,
            OrderActions,
            OrderLogs,
            OrderDetails,
            OrderPersons,
            OrderComments,
            OrderChat,
            OrderPrice,
            OrderAnalyticsData,
            OrderPartnerError,
            OrderDelaysAccounting
        },

        data() {
            return {
                loading: true,
                order: {},
                payments: [],
                orderUnmodified: {},
                refund: {},
                logs: [],
                lastStatusUpdate: '',
                timerId: null
            };
        },

        computed: {
            orderTypeId() {
                switch (this.order.type) {
                case ORDER_TYPE.RESTAURANT_DELIVERY:
                    return 'rd';
                case ORDER_TYPE.FLOWERS_DELIVERY:
                    return 'fd';
                case ORDER_TYPE.LAUNDRY_DELIVERY:
                    return 'ld';
                case ORDER_TYPE.GROCER_DELIVERY:
                    return 'gd';
                case ORDER_TYPE.PHARMACY_DELIVERY:
                    return 'pd';
                case ORDER_TYPE.ASSISTANT_HELP:
                    return 'ah';
                case ORDER_TYPE.ENTITY_DELIVERY:
                    return 'ed';
                default: return '';
                }
            },
            orderItems() {
                if(this.orderTypeId) {
                    return this.order[this.orderTypeId].items;
                }
                return [];
            },
            adminOnly() {
                return !JSON.parse(localStorage.getItem('settings'))
                    .is_business_admin;
            }
        },

        watch: {},

        async mounted() {
            const orderId = parseInt(this.$route.params.id);
            try {
                const orderResp = await Vue.axios.get(`/orders/${orderId}`);
                this.order = JSON.parse(JSON.stringify(orderResp.data));
                this.orderUnmodified = JSON.parse(JSON.stringify(orderResp.data));

                if(!this.order.delays_accounting) {
                    this.order.delays_accounting = [];
                }
                if(!this.order.delay_categories) {
                    this.order.delay_categories = [];
                }

                try {
                    const paymentResp = await Vue.axios.get(`/orders/${orderId}/payments`);
                    this.payments = paymentResp.data;
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка при загрузке данных о платежах.');
                }

                try {
                    const logsResp = await Vue.axios.get(`/orders/${orderId}/logs`);
                    this.logs = logsResp.data;
                    this.lastStatusUpdate = this.order.updated_at;
                    if(this.logs) {
                        this.lastStatusUpdate = this.logs.sort((a, b) => moment(b.created_at).diff(moment(a.created_at)))[0].created_at;
                    }
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка при загрузке данных о статусах.');
                }

                this.calculateRefund();
            } catch (err) {
                this.$root.$emit('showError', 'Ошибка при загрузке данных о заказе.');
                this.$router.push('/orders');
            }
            this.loading = false;
        },

        methods: {
            async fetchOrderData() {
                const orderId = parseInt(this.$route.params.id);
                try {
                    const orderResp = await Vue.axios.get(`/orders/${orderId}`);
                    this.order = JSON.parse(JSON.stringify(orderResp.data));
                    this.orderUnmodified = JSON.parse(JSON.stringify(orderResp.data));

                    try {
                        const paymentResp = await Vue.axios.get(`/orders/${orderId}/payments`);
                        this.payments = paymentResp.data;
                    } catch (err) {
                        this.$root.$emit('showError', 'Ошибка при загрузке данных о платежах.');
                    }

                    try {
                        const logsResp = await Vue.axios.get(`/orders/${orderId}/logs`);
                        this.logs = logsResp.data;
                        this.lastStatusUpdate = this.order.updated_at;
                        if(this.logs) {
                            this.lastStatusUpdate = this.logs.sort((a, b) => moment(b.created_at).diff(moment(a.created_at)))[0].created_at;
                        }
                    } catch (err) {
                        this.$root.$emit('showError', 'Ошибка при загрузке данных о статусах.');
                    }

                    this.calculateRefund();
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка при загрузке данных о заказе.');
                    this.$router.push('/orders');
                }
                this.loading = false;
            },
            async calculateRefund() {
                const payments = this.payments
                    .filter(item => (item.status === ORDER_PAYMENT_STATUS.SUCCESS) && !item.tips);

                let paymentsAmount = 0;
                for(let i=0; i < payments.length; i++) {
                    paymentsAmount += (1 * payments[i].price);
                }
                const refund = +this.order.price - paymentsAmount;
                this.refund.amount = (refund < 0) ? Math.abs(refund).toFixed(2) : 0;
                this.refund.label = (refund > 0) ? `К списанию +${ Math.abs(refund).toFixed(2) }р` : ((refund < 0) ? `К возврату -${ Math.abs(refund).toFixed(2) }р` : '');
            },
            async orderUpdate(value) {
                if (value) {    
                    this.order = Object.assign({}, value);
                }
                const orderId = parseInt(this.$route.params.id);
                if (this?.order?.status === 25) {
                    this.order.status = 5;
                }
                try {
                    const { data } = await Vue.axios.post(
                        `/orders/${orderId}/update`,
                        this.order
                    );
                    this.order = JSON.parse(JSON.stringify(data));
                    this.orderUnmodified = JSON.parse(JSON.stringify(data));
                    this.$root.$emit('showAlert', 'Заказ успешно обновлен.');
                    location.reload();
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка сохранения.');
                }
            },
            async updatePartnerError(value) {
                try {
                    await Vue.axios.post(
                        `/orders/${orderId}/partner-error`,
                        { partner_error: value }
                    );

                    this.$root.$emit('showAlert', 'Успешно.');
                    location.reload();
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка сохранения.');
                }
            },
            async orderRefund(refund) {
                if (this?.order?.status === 25) {
                    this.order.status = 5;
                }
                const { data } = await Vue.axios.post(
                    `/orders/${this.order.id}/refund`,
                    { amount: refund }
                );
                if(data.error) {
                    this.$root.$emit('showError', 'Ошибка возврата. ' + data.error);
                } else {
                    this.$root.$emit('showAlert', 'Возврат успешно выполнен.');
                    location.reload();
                }
            },
            async paymentConfirm() {
                this.loading = true;
                if (this?.order?.status === 25) {
                    this.order.status = 5;
                }
                const { data } = await Vue.axios.post(
                    `/orders/${this.order.id}/confirm-pay`
                );
                if (data && data.payLink) {
                    this.$root.$emit('showAlert', (data.message || 'Создана ссылка на доплату.') + ' ' + data.payLink);
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        try {
                            await navigator.clipboard.writeText(data.payLink);
                        } catch (copyError) {}
                    }
                    location.reload();
                } else if(data.error) {
                    this.$root.$emit('showError', 'Ошибка корректировки. ' + data.error);
                } else {
                    this.$root.$emit('showAlert', 'Платеж успешно скорректорован.');
                    location.reload();
                }
                this.loading = false;
            },
            async updateOrderInfo() {
                await this.fetchOrderData();
                this.$root.$emit('showAlert', 'Заказ успешно обновлен.');
            },
            async orderCopy() {
                if (this?.order?.status === 25) {
                    this.order.status = 5;
                }
                const orderId = parseInt(this.$route.params.id);
                try {
                    const { data } = await Vue.axios.post(`/orders/${orderId}/copy`);
                    this.$router.push(`/orders/${data.order_id}`);
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка копирования.');
                }
            },
            orderCancel() {
                this.order = JSON.parse(JSON.stringify(this.orderUnmodified));
                if (this?.order?.status === 25) {
                    this.order.status = 5;
                }
            },
        },
    };
</script>

<style scoped>
.preloader {
    display: flex;
    width: 100%;
    height: 70vh;
    justify-content: center;
    align-items: center;
}
</style>
